import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuildoutLogo } from 'helpers/custom_fontawesome_icons';

import OffMarketResultsCardList from './off_market_results_card_list';
import OffMarketResultsTable, { ALL_TABLE_FIELDS, DEFAULT_TABLE_FIELDS } from './off_market_results_table';
import OffMarketResultsHeader from './off_market_results_header';
import type { RootState } from '../../store/root_reducer';

type OffMarketResultsProps = {
  allOffMarketResults: RootState['results']['allOffMarketResults'];
  allResults: RootState['results']['allOffMarketResults'];
  containerRef: React.RefObject<HTMLDivElement>;
  searchTableColumnKeys: RootState['ui']['searchTableColumnKeys'];
  showTableView: boolean;
}

function OffMarketResults({
  allOffMarketResults,
  allResults,
  containerRef,
  searchTableColumnKeys,
  showTableView
}: OffMarketResultsProps) {
  const [selectedPropertyIds, setSelectedPropertyIds] = useState(new Set<string>());
  const [selectAll, setSelectAll] = useState(false);

  const searchTableFields =
    searchTableColumnKeys === null
      ? [...DEFAULT_TABLE_FIELDS]
      : searchTableColumnKeys
        ?.map(key => ALL_TABLE_FIELDS.find(field => field.value === key))
        .filter(field => field !== undefined);

  useEffect(() => {
    setSelectedPropertyIds(new Set());
    setSelectAll(false);
  }, [allOffMarketResults]);

  const handleSelectToggle = (propertyId) => {
    if (selectAll) {
      setSelectAll(false);
      const ids = new Set<string>();
      allOffMarketResults && allOffMarketResults.forEach((result) => {
        if (propertyId !== result.propertyId) ids.add(result.propertyId);
      });

      setSelectedPropertyIds(ids);
    } else if (selectedPropertyIds.has(propertyId)) {
      setSelectedPropertyIds(prev => new Set([...prev].filter(id => id !== propertyId)));
    } else {
      setSelectedPropertyIds(previousState => new Set([...previousState, propertyId]));
    }
  };

  const handleToggleAllSelected = () => {
    setSelectedPropertyIds(new Set());
    setSelectAll(!selectAll);
  };

  const COMMON_OFF_MARKET_HEADER_PROPS = {
    selectAll,
    selectedPropertyIds,
    onToggleAllSelected: handleToggleAllSelected
  };

  const COMMON_OFF_MARKET_RESULTS_PROPS = {
    containerRef,
    selectAll,
    selectedPropertyIds,
    onSelectToggle: handleSelectToggle
  };

  if (!allResults) {
    return (
      <div className="text-center py-4">
        <FontAwesomeIcon beatFade className="text-primary" icon={faBuildoutLogo} size="3x" />
      </div>
    );
  }

  return (
    <div className="h-100 d-flex flex-column">
      {!showTableView && (
        <>
          <OffMarketResultsHeader {...COMMON_OFF_MARKET_HEADER_PROPS} />
          <OffMarketResultsCardList {...COMMON_OFF_MARKET_RESULTS_PROPS} />
        </>
      )}
      {showTableView && (
        <>
          <OffMarketResultsHeader {...COMMON_OFF_MARKET_HEADER_PROPS} searchTableFields={searchTableFields} />
          <OffMarketResultsTable
            {...COMMON_OFF_MARKET_RESULTS_PROPS}
            searchTableFields={searchTableFields || []}
            onSelectAllToggle={handleToggleAllSelected}
          />
        </>
      )}

    </div>
  );
}

const mapStateToProps = ({
  results: {
    offMarketResults,
    allOffMarketResults
  },
  ui: { showTableView, searchTableColumnKeys }
}) => {
  return {
    allOffMarketResults,
    allResults: offMarketResults,
    searchTableColumnKeys,
    showTableView
  };
};
export default connect(mapStateToProps)(OffMarketResults);
